import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  Page,
  Layout,
  Card,
  Banner,
  Badge,
  Button,
  Text,
  BlockStack,
  InlineStack,
  InlineGrid,
  ProgressBar,
  Icon,
  Spinner,
  SkeletonDisplayText,
  SkeletonBodyText,
  Divider,
} from "@shopify/polaris";
import {
  ProductIcon,
  LinkIcon,
  ChartVerticalFilledIcon,
  SettingsIcon,
  PersonIcon,
  CheckIcon,
  ArrowRightIcon,
} from "@shopify/polaris-icons";
import {
  useShopifyFetch,
  ShopInfo,
  AnalyticsOverview,
} from "../hooks/useShopifyFetch";
import { OnboardingWizard } from "../components/OnboardingWizard";
import {
  overallQuotaLevel,
  quotaLevelFromFlags,
  UsageBreakdown,
} from "../utils/quota";

interface UsageResponse {
  usage?: UsageBreakdown;
  overage?: {
    in_overage: boolean;
    billed: boolean;
    usage_capped_amount: number;
  };
}

/** Keep internal navigation inside the embedded app by preserving the host/shop
 *  query string App Bridge relies on. */
const withSearch = (path: string) => `${path}${window.location.search}`;

interface RankedRow {
  id: string;
  name: string;
  conversions: number;
  revenue: number;
}

const StatCard = ({
  label,
  value,
  sublabel,
  loading,
}: {
  label: string;
  value: string;
  sublabel: string;
  loading: boolean;
}) => (
  <Card>
    <BlockStack gap="200">
      <Text as="span" variant="bodySm" tone="subdued">
        {label}
      </Text>
      {loading ? (
        <SkeletonDisplayText size="medium" />
      ) : (
        <Text as="p" variant="heading2xl">
          {value}
        </Text>
      )}
      <Text as="span" variant="bodySm" tone="subdued">
        {sublabel}
      </Text>
    </BlockStack>
  </Card>
);

const RankedList = ({
  title,
  rows,
  loading,
  emptyText,
  onViewAll,
}: {
  title: string;
  rows: RankedRow[];
  loading: boolean;
  emptyText: string;
  onViewAll: () => void;
}) => (
  <Card>
    <BlockStack gap="300">
      <InlineStack align="space-between" blockAlign="center">
        <Text as="h3" variant="headingSm">
          {title}
        </Text>
        <Button variant="plain" onClick={onViewAll}>
          View all
        </Button>
      </InlineStack>
      <Divider />
      {loading ? (
        <SkeletonBodyText lines={4} />
      ) : rows.length > 0 ? (
        <BlockStack gap="200">
          {rows.slice(0, 5).map((row, index) => (
            <InlineStack
              key={row.id}
              align="space-between"
              blockAlign="center"
              wrap={false}
            >
              <InlineStack gap="200" blockAlign="center" wrap={false}>
                <Badge tone="info">{String(index + 1)}</Badge>
                <Text as="span" variant="bodyMd" truncate>
                  {row.name}
                </Text>
              </InlineStack>
              <InlineStack gap="400" blockAlign="center" wrap={false}>
                <BlockStack gap="0" inlineAlign="end">
                  <Text as="span" variant="bodyMd" fontWeight="semibold">
                    {row.conversions.toLocaleString()}
                  </Text>
                  <Text as="span" variant="bodySm" tone="subdued">
                    Conv
                  </Text>
                </BlockStack>
                <BlockStack gap="0" inlineAlign="end">
                  <Text as="span" variant="bodyMd" fontWeight="semibold">
                    ${row.revenue.toLocaleString()}
                  </Text>
                  <Text as="span" variant="bodySm" tone="subdued">
                    Revenue
                  </Text>
                </BlockStack>
              </InlineStack>
            </InlineStack>
          ))}
        </BlockStack>
      ) : (
        <Text as="p" variant="bodyMd" tone="subdued">
          {emptyText}
        </Text>
      )}
    </BlockStack>
  </Card>
);

export default function Dashboard() {
  const [showOnboarding, setShowOnboarding] = useState(
    () => !localStorage.getItem("affilync_onboarding_complete"),
  );
  const { get } = useShopifyFetch();
  const navigate = useNavigate();

  const {
    data: shop,
    isLoading: shopLoading,
    error: shopError,
  } = useQuery<ShopInfo>({
    queryKey: ["shop"],
    queryFn: () => get<ShopInfo>("/api/shop"),
  });

  const { data: analytics, isLoading: analyticsLoading } =
    useQuery<AnalyticsOverview>({
      queryKey: ["analytics", "overview"],
      queryFn: () => get("/api/analytics/overview?days=30"),
      enabled: !!shop?.is_connected,
    });

  const { data: usage } = useQuery<UsageResponse>({
    queryKey: ["billing-usage"],
    queryFn: () => get("/api/billing/usage"),
    enabled: !!shop?.is_connected,
  });

  const usageQuotaLevel = overallQuotaLevel(usage?.usage);
  const flagQuotaLevel = quotaLevelFromFlags(
    usage as unknown as Record<string, unknown>,
  );
  const quotaLevel = flagQuotaLevel === "over" ? "over" : usageQuotaLevel;

  // Billed overage (usage plans): conversions past the limit are charged, not
  // blocked. Surface a distinct notice so a real charge is never a surprise.
  const overage = usage?.overage;
  const inBilledOverage = !!overage?.in_overage && !!overage?.billed;

  const setupSteps = [
    { label: "Install app", completed: true },
    { label: "Connect Affilync", completed: shop?.is_connected || false },
    { label: "Import products", completed: false },
    {
      label: "Configure attribution",
      completed: shop?.settings?.cookie_duration_days !== undefined,
    },
  ];
  const completedSteps = setupSteps.filter((s) => s.completed).length;
  const setupProgress = (completedSteps / setupSteps.length) * 100;

  if (shopLoading) {
    return (
      <Page title="Dashboard">
        <Card>
          <InlineStack align="center" blockAlign="center" gap="200">
            <Spinner accessibilityLabel="Loading dashboard" size="small" />
            <Text as="span" variant="bodyMd" tone="subdued">
              Loading your dashboard…
            </Text>
          </InlineStack>
        </Card>
      </Page>
    );
  }

  if (shopError) {
    return (
      <Page title="Dashboard">
        <Banner tone="critical" title="Unable to load dashboard">
          <p>
            Failed to load shop information. Please refresh the page or contact
            support.
          </p>
        </Banner>
      </Page>
    );
  }

  const connected = !!shop?.is_connected;

  return (
    <Page
      title={shop?.shop_name || shop?.shop_domain || "Dashboard"}
      subtitle="Affiliate marketing for your Shopify store"
      titleMetadata={
        <Badge tone={connected ? "success" : "attention"}>
          {connected ? "Connected" : "Not connected"}
        </Badge>
      }
    >
      <OnboardingWizard
        open={showOnboarding}
        onComplete={() => {
          localStorage.setItem("affilync_onboarding_complete", "1");
          setShowOnboarding(false);
        }}
        shopName={shop?.shop_name}
      />

      <Layout>
        {inBilledOverage && (
          <Layout.Section>
            <Banner
              tone="warning"
              title="You've entered usage overage"
              action={{
                content: "Upgrade plan",
                onAction: () => navigate(withSearch("/billing")),
              }}
            >
              <p>
                {`You've used all the conversions included in your plan. Additional conversions this month are billed as overage${
                  overage && overage.usage_capped_amount > 0
                    ? `, up to $${overage.usage_capped_amount.toLocaleString()}`
                    : ""
                }. Upgrade to raise your included limit.`}
              </p>
            </Banner>
          </Layout.Section>
        )}

        {!inBilledOverage && quotaLevel !== "ok" && (
          <Layout.Section>
            <Banner
              tone="warning"
              title={
                quotaLevel === "over"
                  ? "You're over your plan limit"
                  : "You're approaching your plan limit"
              }
              action={{
                content: "Upgrade plan",
                onAction: () => navigate(withSearch("/billing")),
              }}
            >
              <p>
                {quotaLevel === "over"
                  ? "You've reached your plan's limit. New conversions and affiliates are blocked until you upgrade."
                  : "You're close to your plan's limit. Upgrade now to keep tracking conversions and affiliates."}
              </p>
            </Banner>
          </Layout.Section>
        )}

        {setupProgress < 100 && (
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <BlockStack gap="100">
                  <Text as="h2" variant="headingMd">
                    Complete your setup
                  </Text>
                  <Text as="p" variant="bodyMd" tone="subdued">
                    Connect your Affilync brand account to start tracking
                    affiliate conversions and syncing products to the
                    marketplace.
                  </Text>
                </BlockStack>

                <BlockStack gap="150">
                  <ProgressBar
                    progress={setupProgress}
                    size="small"
                    tone="primary"
                  />
                  <Text as="span" variant="bodySm" tone="subdued">
                    {completedSteps} of {setupSteps.length} steps completed
                  </Text>
                </BlockStack>

                <BlockStack gap="200">
                  {setupSteps.map((step) => (
                    <InlineStack key={step.label} gap="200" blockAlign="center">
                      {step.completed ? (
                        <Icon source={CheckIcon} tone="success" />
                      ) : (
                        <Icon source={ArrowRightIcon} tone="subdued" />
                      )}
                      <Text
                        as="span"
                        variant="bodyMd"
                        tone={step.completed ? undefined : "subdued"}
                      >
                        {step.label}
                      </Text>
                    </InlineStack>
                  ))}
                </BlockStack>

                {!connected && (
                  <InlineStack>
                    <Button
                      variant="primary"
                      onClick={() => navigate(withSearch("/settings"))}
                    >
                      Connect Affilync account
                    </Button>
                  </InlineStack>
                )}
              </BlockStack>
            </Card>
          </Layout.Section>
        )}

        {connected && (
          <Layout.Section>
            <InlineGrid columns={{ xs: 1, sm: 3 }} gap="400">
              <StatCard
                label="Conversions"
                value={(analytics?.conversions || 0).toLocaleString()}
                sublabel="Last 30 days"
                loading={analyticsLoading}
              />
              <StatCard
                label="Revenue"
                value={`$${(analytics?.revenue || 0).toLocaleString()}`}
                sublabel="Affiliate attributed"
                loading={analyticsLoading}
              />
              <StatCard
                label="Clicks"
                value={(analytics?.clicks || 0).toLocaleString()}
                sublabel="Affiliate link clicks"
                loading={analyticsLoading}
              />
            </InlineGrid>
          </Layout.Section>
        )}

        {connected && (
          <Layout.Section>
            <InlineGrid columns={{ xs: 1, md: 2 }} gap="400">
              <RankedList
                title="Top affiliates"
                loading={analyticsLoading}
                rows={(analytics?.top_affiliates || []) as RankedRow[]}
                emptyText="No affiliate data yet. Start promoting your products!"
                onViewAll={() => navigate(withSearch("/analytics"))}
              />
              <RankedList
                title="Top products"
                loading={analyticsLoading}
                rows={(analytics?.top_products || []).map((p) => ({
                  id: p.id,
                  name: p.title,
                  conversions: p.conversions,
                  revenue: p.revenue,
                }))}
                emptyText="No product data yet. Sync your products to get started!"
                onViewAll={() => navigate(withSearch("/products"))}
              />
            </InlineGrid>
          </Layout.Section>
        )}

        <Layout.Section>
          <Card>
            <BlockStack gap="300">
              <Text as="h3" variant="headingSm">
                Quick actions
              </Text>
              <InlineStack gap="300">
                <Button
                  icon={ProductIcon}
                  onClick={() => navigate(withSearch("/products"))}
                >
                  Manage products
                </Button>
                <Button
                  icon={LinkIcon}
                  onClick={() => navigate(withSearch("/links"))}
                >
                  Generate links
                </Button>
                <Button
                  icon={ChartVerticalFilledIcon}
                  onClick={() => navigate(withSearch("/analytics"))}
                >
                  View analytics
                </Button>
                <Button
                  icon={SettingsIcon}
                  onClick={() => navigate(withSearch("/settings"))}
                >
                  Settings
                </Button>
                <Button
                  icon={PersonIcon}
                  onClick={() => navigate(withSearch("/analytics"))}
                >
                  Affiliates
                </Button>
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
